import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import axios from 'axios';
import * as Sentry from '@sentry/react';

import { CREATE_SIGNED_S3_UPLOAD } from 'src/operations/mutations/uploadFileS3';
import { UPDATE_AI_TOPIC_USER_ASSIGNMENT } from 'src/operations/mutations/aiTopic';
import { AI_TOPIC_USER_ASSIGNMENT_DETAILS } from 'src/operations/queries/aiTopic';
import { getCurrentToken } from 'src/utils';

const DATABASE_NAME = 'culp-ai-speaking-audio-uploads';
const DATABASE_VERSION = 1;
const STORE_NAME = 'pending-uploads';
const QUEUE_EVENT_NAME = 'ai-speaking-audio-upload-queued';
const API_TIMEOUT_MS = 30000;
const UPLOAD_TIMEOUT_MS = 300000;
const RETRY_INTERVAL_MS = 60000;
const MAX_RETRY_DELAY_MS = 300000;
const UNRELEASED_ENTRY_RECOVERY_MS = 300000;

let activeQueueProcessor = null;

const withTimeout = (promise, timeoutMs, label) => {
  let timeout = null;
  const timeoutPromise = new Promise((resolve, reject) => {
    timeout = setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeout) clearTimeout(timeout);
  });
};

const openDatabase = () =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser.'));
      return;
    }

    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('userIdx', 'userIdx', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Unable to open the audio upload queue.'));
  });

const runStoreRequest = async (mode, operation) => {
  const database = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const store = transaction.objectStore(STORE_NAME);
      let result;

      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error || new Error('Audio upload queue transaction failed.'));
      transaction.onabort = () => reject(transaction.error || new Error('Audio upload queue transaction was aborted.'));

      try {
        const request = operation(store);
        if (request) {
          request.onsuccess = () => {
            result = request.result;
          };
          request.onerror = () => reject(request.error || new Error('Audio upload queue request failed.'));
        }
      } catch (error) {
        reject(error);
      }
    });
  } finally {
    database.close();
  }
};

const createQueueId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `audio_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

export const enqueueAiSpeakingAudioUpload = async (upload) => {
  const now = Date.now();
  const entry = {
    id: upload?.id || createQueueId(),
    assignmentIdx: Number(upload?.assignmentIdx),
    userIdx: Number(upload?.userIdx),
    blob: upload?.blob,
    contentType: upload?.contentType || 'audio/webm',
    fileName: upload?.fileName,
    publicUrl: upload?.publicUrl || null,
    signedUploadUrl: upload?.signedUploadUrl || null,
    uploadedUrl: upload?.uploadedUrl || null,
    createdAt: now,
    updatedAt: now,
    attempts: 0,
    nextAttemptAt: 0,
    lastError: null,
    ready: Boolean(upload?.ready),
  };

  await runStoreRequest('readwrite', (store) => store.put(entry));
  if (entry.ready && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(QUEUE_EVENT_NAME));
  }
  return entry;
};

const putQueueEntry = (entry) =>
  runStoreRequest('readwrite', (store) =>
    store.put({
      ...entry,
      updatedAt: Date.now(),
    }),
  );

const deleteQueueEntry = (id) => runStoreRequest('readwrite', (store) => store.delete(id));

const getQueueEntries = () => runStoreRequest('readonly', (store) => store.getAll());

const getQueueEntry = (id) => runStoreRequest('readonly', (store) => store.get(id));

export const releaseAiSpeakingAudioUpload = async (id) => {
  if (!id) return;
  const entry = await getQueueEntry(id);
  if (!entry) return;
  await putQueueEntry({
    ...entry,
    ready: true,
    nextAttemptAt: 0,
  });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(QUEUE_EVENT_NAME));
  }
};

const parseRubricData = (rubricData) => {
  if (rubricData && typeof rubricData === 'object') return rubricData;
  try {
    return JSON.parse(rubricData || '{}');
  } catch (_error) {
    return {};
  }
};

const requestSignedUploadTarget = async (client, entry) => {
  const result = await withTimeout(
    client.mutate({
      mutation: CREATE_SIGNED_S3_UPLOAD,
      variables: {
        name: entry.fileName,
        contentType: entry.contentType,
        replace: true,
      },
    }),
    API_TIMEOUT_MS,
    'Signed S3 upload request',
  );
  const signedUploadUrl = result?.data?.createSignedS3Upload;
  if (!signedUploadUrl) throw new Error('Failed to get a signed S3 upload URL.');
  return {
    signedUploadUrl,
    publicUrl: signedUploadUrl.split('?')[0],
  };
};

const uploadBlob = async (signedUploadUrl, entry) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeout = controller ? setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS) : null;
  try {
    const response = await fetch(signedUploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': entry.contentType,
        'x-amz-acl': 'public-read',
      },
      body: entry.blob,
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!response.ok) {
      const error = new Error(`S3 PUT failed with status ${response.status}`);
      error.status = response.status;
      throw error;
    }
  } finally {
    if (timeout) clearTimeout(timeout);
  }
};

const appendUploadedUrl = async (client, entry, uploadedUrl) => {
  const detailsResult = await withTimeout(
    client.query({
      query: AI_TOPIC_USER_ASSIGNMENT_DETAILS,
      variables: { idx: entry.assignmentIdx },
      fetchPolicy: 'network-only',
    }),
    API_TIMEOUT_MS,
    'Latest assignment request',
  );
  const assignment = detailsResult?.data?.aiTopicUserAssignmentDetails;
  if (!assignment) throw new Error('Unable to load the latest assignment before saving its audio URL.');

  const rubricData = parseRubricData(assignment.rubric_data);
  const existingUrls = Array.isArray(rubricData.urls) ? rubricData.urls : [];
  const urls = existingUrls.includes(uploadedUrl) ? existingUrls : [...existingUrls, uploadedUrl];

  await withTimeout(
    client.mutate({
      mutation: UPDATE_AI_TOPIC_USER_ASSIGNMENT,
      variables: {
        idx: entry.assignmentIdx,
        rubric_data: JSON.stringify({
          ...rubricData,
          urls,
        }),
      },
    }),
    API_TIMEOUT_MS,
    'Assignment audio URL update',
  );
};

const triggerMp3Conversion = (uploadedUrl, assignmentIdx) => {
  const token = getCurrentToken();
  if (!token || !uploadedUrl) return;

  axios
    .post(
      'https://kazuclgtjwd34k7dkfgpcztiwq0vvyrg.lambda-url.ap-northeast-2.on.aws/',
      {
        token,
        s3Url: uploadedUrl,
        deleteOriginal: true,
        assignment_idx: assignmentIdx,
        backend_url: process.env.REACT_APP_BACKEND_HOST,
      },
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 1000,
      },
    )
    .catch(() => {});
};

const retryDelay = (attempts) => Math.min(MAX_RETRY_DELAY_MS, 30000 * Math.max(1, 2 ** Math.min(attempts - 1, 4)));

const processQueueEntry = async (client, originalEntry) => {
  let entry = originalEntry;
  try {
    if (!entry.uploadedUrl) {
      let signedUploadUrl = entry.signedUploadUrl;
      let publicUrl = entry.publicUrl;
      if (!signedUploadUrl || !publicUrl) {
        const signedTarget = await requestSignedUploadTarget(client, entry);
        signedUploadUrl = signedTarget.signedUploadUrl;
        publicUrl = signedTarget.publicUrl;
        entry = { ...entry, signedUploadUrl, publicUrl };
        await putQueueEntry(entry);
      }

      try {
        await uploadBlob(signedUploadUrl, entry);
      } catch (uploadError) {
        if (uploadError?.status === 401 || uploadError?.status === 403) {
          const signedTarget = await requestSignedUploadTarget(client, entry);
          entry = { ...entry, ...signedTarget };
          await putQueueEntry(entry);
          await uploadBlob(signedTarget.signedUploadUrl, entry);
          publicUrl = signedTarget.publicUrl;
        } else {
          throw uploadError;
        }
      }

      entry = {
        ...entry,
        uploadedUrl: publicUrl,
        publicUrl,
        signedUploadUrl: null,
        lastError: null,
        nextAttemptAt: 0,
      };
      await putQueueEntry(entry);
    }

    await appendUploadedUrl(client, entry, entry.uploadedUrl);
    triggerMp3Conversion(entry.uploadedUrl, entry.assignmentIdx);
    await deleteQueueEntry(entry.id);
  } catch (error) {
    const attempts = Number(entry.attempts || 0) + 1;
    const expiredSignedUrl = error?.status === 401 || error?.status === 403;
    await putQueueEntry({
      ...entry,
      attempts,
      nextAttemptAt: Date.now() + retryDelay(attempts),
      lastError: String(error?.message || error),
      ...(expiredSignedUrl ? { signedUploadUrl: null } : {}),
    });
    Sentry.captureException(error, {
      tags: { feature: 'ai-speaking', action: 'background-audio-upload' },
      extra: {
        assignment_idx: entry.assignmentIdx,
        queue_id: entry.id,
        attempts,
        uploaded: Boolean(entry.uploadedUrl),
      },
    });
  }
};

export const processAiSpeakingAudioUploadQueue = (client, userIdx) => {
  if (typeof indexedDB === 'undefined') return Promise.resolve();
  if (activeQueueProcessor) return activeQueueProcessor;

  activeQueueProcessor = (async () => {
    if (!client || !Number.isFinite(Number(userIdx)) || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
    const entries = await getQueueEntries();
    const now = Date.now();
    const matchingEntries = (entries || [])
      .filter((entry) => Number(entry.userIdx) === Number(userIdx))
      .filter((entry) => entry.ready || Number(entry.createdAt || 0) <= now - UNRELEASED_ENTRY_RECOVERY_MS)
      .filter((entry) => !entry.nextAttemptAt || entry.nextAttemptAt <= now)
      .sort((left, right) => Number(left.createdAt || 0) - Number(right.createdAt || 0));

    for (const entry of matchingEntries) {
      await processQueueEntry(client, entry);
    }
  })()
    .catch((error) => {
      console.error('Unable to process the AI speaking audio upload queue:', error);
    })
    .finally(() => {
      activeQueueProcessor = null;
    });

  return activeQueueProcessor;
};

export const useAiSpeakingAudioUploadProcessor = (userIdx) => {
  const client = useApolloClient();

  useEffect(() => {
    if (!Number.isFinite(Number(userIdx))) return undefined;

    const processQueue = () => {
      void processAiSpeakingAudioUploadQueue(client, userIdx);
    };
    const processVisibleQueue = () => {
      if (document.visibilityState === 'visible') processQueue();
    };

    processQueue();
    const interval = setInterval(processQueue, RETRY_INTERVAL_MS);
    window.addEventListener('online', processQueue);
    window.addEventListener(QUEUE_EVENT_NAME, processQueue);
    document.addEventListener('visibilitychange', processVisibleQueue);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', processQueue);
      window.removeEventListener(QUEUE_EVENT_NAME, processQueue);
      document.removeEventListener('visibilitychange', processVisibleQueue);
    };
  }, [client, userIdx]);
};
